import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import { authApi, supportApi, locationsApi } from '../services/api'
import toast from 'react-hot-toast'
import TicketDetailModal from '../components/TicketDetailModal'
import {
  CameraIcon,
  UserIcon,
  EnvelopeIcon,
  CheckCircleIcon,
  XCircleIcon,
  PencilIcon,
  MapPinIcon,
  BuildingOfficeIcon,
  ShieldCheckIcon,
  DocumentChartBarIcon,
  ArrowRightOnRectangleIcon,
  BeakerIcon,
  LifebuoyIcon,
  PlusIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline'
import { useDevMode } from '@/contexts/DevModeContext'

interface ProfileForm {
  name: string
  email: string
  pictureUrl?: string
}

interface UserProfile {
  id: string
  email: string
  name: string
  pictureUrl?: string
  isVerified: boolean
  userOrganizations?: Array<{
    id: string
    role: string
    status: string
    organization: {
      id: string
      name: string
      logoUrl?: string
    }
  }>
}

interface ProfileData {
  id: string
  name: string
  email: string
  phone?: string
  pictureUrl?: string
  organization: {
    id: string
    name: string
    role: string
  }
  stats: {
    totalCheckIns: number
    totalIncidents: number
    totalHourlyReports: number
    averageShiftDuration: string
    currentStreak: number
  }
  recentActivity: Array<{
    id: string
    type: 'checkin' | 'checkout' | 'incident' | 'hourly_report'
    location: string
    timestamp: string
    description: string
  }>
}

interface ProfileProps {
  user: any
  logout: () => Promise<void>
}

export default function Profile({ user, logout }: ProfileProps) {
  const navigate = useNavigate()
  const { isDevModeEnabled, setDevModeEnabled } = useDevMode()
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [updating, setUpdating] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [photoUploadLoading, setPhotoUploadLoading] = useState(false)
  const [logoutLoading, setLogoutLoading] = useState(false)
  const [showSupportModal, setShowSupportModal] = useState(false)
  const [userLocations, setUserLocations] = useState<any[]>([])
  const [supportTickets, setSupportTickets] = useState<any[]>([])
  const [supportLoading, setSupportLoading] = useState(false)
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null)
  const [showTicketDetail, setShowTicketDetail] = useState(false)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<ProfileForm>()

  const {
    register: registerSupport,
    handleSubmit: handleSubmitSupport,
    reset: resetSupport,
    formState: { errors: supportErrors, isSubmitting: isSupportSubmitting }
  } = useForm<{
    subject: string
    description: string
    priority: 'LOW' | 'MEDIUM' | 'HIGH'
  }>()

  useEffect(() => {
    loadProfile()
    loadUserLocations()
    loadSupportTickets()
  }, [])

  const loadProfile = async () => {
    try {
      setLoading(true)
      const response = await authApi.getProfile()
      setProfile(response)
      reset({
        name: response.name,
        email: response.email,
        pictureUrl: response.pictureUrl
      })
    } catch (error: any) {
      toast.error('Failed to load profile')
      console.error('Error loading profile:', error)
    } finally {
      setLoading(false)
    }
  }

  const onSubmit = async (data: ProfileForm) => {
    try {
      setUpdating(true)
      // Send name and email if they have changed
      const updateData: any = {
        name: data.name
      }

      // Only include email if it has changed
      if (data.email && data.email !== profile?.email) {
        updateData.email = data.email
      }

      await authApi.updateProfile(updateData)
      toast.success('Profile updated successfully')
      setIsEditing(false)
      loadProfile()
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to update profile')
    } finally {
      setUpdating(false)
    }
  }

  const handlePhotoUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file')
      return
    }

    try {
      setPhotoUploadLoading(true)
      const formData = new FormData()
      formData.append('photo', file)

      console.log('Uploading photo:', {
        name: file.name,
        size: file.size,
        type: file.type
      })

      const result = await authApi.uploadPhoto(formData)
      console.log('Upload result:', result)

      toast.success('Photo updated successfully')
      loadProfile()
    } catch (error: any) {
      console.error('Photo upload error:', error)
      toast.error('Failed to upload photo')
    } finally {
      setPhotoUploadLoading(false)
    }
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      handlePhotoUpload(file)
    }
  }

  const handleLogout = async () => {
    try {
      setLogoutLoading(true)
      await logout()
      navigate('/login')
    } catch (error) {
      console.error('Logout error:', error)
      toast.error('Failed to sign out')
    } finally {
      setLogoutLoading(false)
    }
  }

  const loadUserLocations = async () => {
    try {
      const locations = await locationsApi.getLocations()
      setUserLocations(locations || [])
    } catch (error) {
      console.error('Error loading user locations:', error)
      setUserLocations([])
    }
  }

  const loadSupportTickets = async () => {
    try {
      setSupportLoading(true);
      const tickets = await supportApi.getTickets();
      setSupportTickets(tickets.data || []);
    } catch (error) {
      console.error('Error loading support tickets:', error);
      setSupportTickets([]);
    } finally {
      setSupportLoading(false);
    }
  }

  const handleCreateSupportTicket = async (data: { subject: string; description: string; priority: 'LOW' | 'MEDIUM' | 'HIGH' }) => {
    try {
      if (!user?.currentOrganization?.id) {
        toast.error('No organization selected')
        return
      }

      // Check if user has no locations and adjust the subject/description accordingly
      if (userLocations.length === 0) {
        const isLocationRequest = data.subject.toLowerCase().includes('location') ||
          data.description.toLowerCase().includes('location') ||
          data.description.toLowerCase().includes('assign')

        if (isLocationRequest) {
          data.subject = '[Location Assignment Request] ' + data.subject
          data.description = `[EMPLOYEE HAS NO LOCATION ASSIGNMENTS] - ${data.description}\n\nNote: This employee currently has no location assignments and is requesting to be assigned to locations.`
        }
      }

      await supportApi.createTicket({
        ...data,
        userEmail: user.email,
        userName: user.name,
        organizationId: user.currentOrganization.id
      })

      toast.success('Support ticket created successfully')
      setShowSupportModal(false)
      resetSupport()
      loadSupportTickets()
    } catch (error: any) {
      console.error('Error creating support ticket:', error)
      toast.error(error.response?.data?.error || 'Failed to create support ticket')
    }
  }

  const handleTicketClick = (ticketId: string) => {
    setSelectedTicketId(ticketId)
    setShowTicketDetail(true)
  }

  const handleCloseTicketDetail = () => {
    setShowTicketDetail(false)
    setSelectedTicketId(null)
  }

  const handleTicketUpdate = () => {
    // Reload support tickets to show updated data
    loadSupportTickets()
  }

  const getRoleColor = (role: string) => {
    switch (role.toLowerCase()) {
      case 'admin':
        return 'from-error to-red-600'
      case 'manager':
        return 'from-warning to-accent-amber'
      case 'employee':
        return 'from-success to-accent-emerald'
      default:
        return 'from-secondary-500 to-secondary-600'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'text-success'
      case 'pending':
        return 'text-warning'
      case 'inactive':
        return 'text-error'
      default:
        return 'text-tech-400'
    }
  }

  if (loading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8 pt-6 pb-8 space-y-6 sm:space-y-8 animate-fade-in">
        {/* Header */}
        <div className="space-y-4">
          <div className="flex items-center space-x-3">
            <div className="p-3 rounded-xl bg-gradient-orange shadow-glow-sm">
              <UserIcon className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-display-sm font-display text-gradient">Profile</h1>
              <p className="text-body text-gray-600 dark:text-tech-400 font-mono">Manage your account</p>
            </div>
          </div>
        </div>

        {/* Loading skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="premium-card p-8">
              <div className="h-6 bg-tech-700 rounded mb-4 w-48"></div>
              <div className="space-y-4">
                {[...Array(3)].map((_, j) => (
                  <div key={j} className="h-16 bg-tech-700 rounded animate-pulse"></div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 pt-6 pb-8 space-y-6 sm:space-y-8 animate-fade-in">
      {/* Header */}
      <div className="space-y-4">
        <div className="flex items-center space-x-3">
          <div className="p-3 rounded-xl bg-gradient-orange shadow-glow-sm animate-pulse-orange">
            <UserIcon className="h-8 w-8 text-white" />
          </div>
          <div>
            <h1 className="text-display-sm font-display text-gradient">Profile</h1>
            <p className="text-body text-gray-600 dark:text-tech-400 font-mono">
              View your account information
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Profile Information */}
        <div className="premium-card p-6 lg:p-8">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 rounded-lg bg-gradient-to-br from-secondary-500 to-secondary-600">
              <UserIcon className="h-5 w-5 text-white" />
            </div>
            <h2 className="text-heading-lg text-gray-900 dark:text-tech-50 font-display">Personal Information</h2>
          </div>

          {isEditing ? (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Profile Photo Upload */}
              <div className="flex flex-col items-center space-y-4">
                <div className="relative">
                  <div className="w-24 h-24 rounded-full overflow-hidden bg-gradient-to-br from-tech-700 to-tech-600 flex items-center justify-center">
                    {profile?.pictureUrl ? (
                      <img
                        src={profile.pictureUrl}
                        alt="Profile"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <UserIcon className="w-12 h-12 text-tech-300" />
                    )}
                  </div>
                  {/* Photo upload disabled */}
                  {/* <label className="absolute bottom-0 right-0 p-2 bg-gradient-orange rounded-full cursor-pointer shadow-glow-sm hover:shadow-glow transition-all duration-300">
                    <CameraIcon className="w-4 h-4 text-white" />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                      disabled={photoUploadLoading}
                    />
                  </label> */}
                  {/* Photo upload loading disabled */}
                  {/* {photoUploadLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                    </div>
                  )} */}
                </div>
              </div>

              {/* Name Field */}
              <div className="space-y-2">
                <label className="block text-body-sm font-medium text-gray-900 dark:text-tech-50">
                  Full Name
                </label>
                <input
                  {...register('name', { required: 'Name is required' })}
                  className="w-full px-4 py-3 bg-white/80 dark:bg-tech-800/50 backdrop-blur-sm border border-gray-300 dark:border-tech-700 rounded-lg text-gray-900 dark:text-tech-100 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 transition-all duration-200"
                />
                {errors.name && (
                  <p className="text-caption text-error">{errors.name.message}</p>
                )}
              </div>

              {/* Email Field */}
              <div className="space-y-2">
                <label className="block text-body-sm font-medium text-gray-900 dark:text-tech-50">
                  Email Address
                </label>
                <input
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^\S+@\S+$/i,
                      message: 'Invalid email address',
                    },
                  })}
                  type="email"
                  className="w-full px-4 py-3 bg-white/80 dark:bg-tech-800/50 backdrop-blur-sm border border-gray-300 dark:border-tech-700 rounded-lg text-gray-900 dark:text-tech-100 focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 transition-all duration-200"
                />
                {errors.email && (
                  <p className="text-caption text-error">{errors.email.message}</p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3">
                <button
                  type="submit"
                  disabled={isSubmitting || updating}
                  className="flex-1 flex items-center justify-center space-x-2 p-3 bg-gradient-to-r from-success to-accent-emerald text-white rounded-lg hover:shadow-glow-lg transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0"
                >
                  {updating ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : (
                    <>
                      <CheckCircleIcon className="w-5 h-5" />
                      <span className="text-body-sm font-medium">Save</span>
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsEditing(false)
                    reset()
                  }}
                  className="flex-1 flex items-center justify-center space-x-2 p-3 bg-gradient-to-r from-tech-700 to-tech-600 text-white rounded-lg hover:shadow-glow transition-all duration-300 hover:-translate-y-0.5"
                >
                  <XCircleIcon className="w-5 h-5" />
                  <span className="text-body-sm font-medium">Cancel</span>
                </button>
              </div>
            </form>
          ) : (
            <div className="space-y-6">
              {/* Profile Photo Display */}
              <div className="flex items-center space-x-4">
                <div className="w-20 h-20 rounded-full overflow-hidden bg-gradient-to-br from-tech-700 to-tech-600 flex items-center justify-center">
                  {profile?.pictureUrl ? (
                    <img
                      src={profile.pictureUrl}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <UserIcon className="w-10 h-10 text-tech-300" />
                  )}
                </div>
                <div>
                  <h3 className="text-heading-md text-gray-900 dark:text-tech-50 font-display">{profile?.name}</h3>
                  <div className="flex items-center space-x-2 mt-1">
                    <EnvelopeIcon className="w-4 h-4 text-gray-600 dark:text-tech-400" />
                    <span className="text-body-sm text-gray-600 dark:text-tech-400">{profile?.email}</span>
                  </div>
                </div>
              </div>

              {/* Verification Status */}
              <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-tech-800/30 rounded-lg">
                {profile?.isVerified ? (
                  <>
                    <div className="p-1 rounded-full bg-success/20">
                      <CheckCircleIcon className="w-4 h-4 text-success" />
                    </div>
                    <span className="text-body-sm text-success font-medium">Email Verified</span>
                  </>
                ) : (
                  <>
                    <div className="p-1 rounded-full bg-warning/20">
                      <XCircleIcon className="w-4 h-4 text-warning" />
                    </div>
                    <span className="text-body-sm text-warning font-medium">Email Not Verified</span>
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Organizations */}
        <div className="premium-card p-6 lg:p-8">
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-2 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600">
              <BuildingOfficeIcon className="h-5 w-5 text-white" />
            </div>
            <h2 className="text-heading-lg text-gray-900 dark:text-tech-50 font-display">Organizations</h2>
          </div>

          <div className="space-y-4">
            {profile?.userOrganizations?.map((userOrg, index) => (
              <div key={userOrg.id} className={`p-4 bg-gradient-to-r ${getRoleColor(userOrg.role)}/10 border border-gray-200 dark:border-tech-700/50 rounded-lg animate-slide-up delay-${index * 100}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    <div className={`p-2 rounded-lg bg-gradient-to-br ${getRoleColor(userOrg.role)}`}>
                      <BuildingOfficeIcon className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h3 className="text-body font-medium text-gray-900 dark:text-tech-50">
                        {userOrg.organization.name}
                      </h3>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className="text-caption font-medium text-gray-600 dark:text-tech-400 capitalize">
                          {userOrg.role}
                        </span>
                        <span className="text-caption">•</span>
                        <span className={`text-caption font-medium capitalize ${getStatusColor(userOrg.status)}`}>
                          {userOrg.status}
                        </span>
                      </div>
                    </div>
                  </div>
                  {userOrg.status === 'active' && (
                    <div className="p-1 rounded-full bg-success/20">
                      <CheckCircleIcon className="w-4 h-4 text-success" />
                    </div>
                  )}
                </div>
              </div>
            )) || (
                <div className="text-center py-8">
                  <BuildingOfficeIcon className="w-12 h-12 text-gray-400 dark:text-tech-500 mx-auto mb-4" />
                  <p className="text-body text-gray-600 dark:text-tech-400">No organizations found</p>
                </div>
              )}
          </div>
        </div>
      </div>

      {/* Support Section */}
      <div className="premium-card p-6 lg:p-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600">
              <LifebuoyIcon className="h-5 w-5 text-white" />
            </div>
            <h2 className="text-heading-lg text-gray-900 dark:text-tech-50 font-display">Support</h2>
          </div>
          <button
            onClick={() => setShowSupportModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            <PlusIcon className="w-4 h-4" />
            <span className="text-sm font-medium">New Ticket</span>
          </button>
        </div>

        {userLocations.length === 0 && (
          <div className="mb-4 p-4 border-2 border-orange-500/30 bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 rounded-lg">
            <div className="flex items-start space-x-3">
              <ExclamationTriangleIcon className="w-5 h-5 text-orange-600 dark:text-orange-400 mt-0.5" />
              <div>
                <h4 className="text-sm font-bold text-orange-800 dark:text-orange-200">No Location Assignments</h4>
                <p className="text-sm text-orange-600 dark:text-orange-400 mt-1">
                  You don't have any location assignments yet. Contact your administrator or create a support ticket to request location access.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-3">
          {supportLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
              <p className="text-gray-600 dark:text-tech-400 mt-2">Loading support tickets...</p>
            </div>
          ) : supportTickets.length > 0 ? (
            supportTickets.slice(0, 3).map((ticket) => (
              <div 
                key={ticket.id} 
                onClick={() => handleTicketClick(ticket.id)}
                className="p-4 bg-gray-50 dark:bg-tech-800 border border-gray-200 dark:border-tech-700 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-tech-700 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-tech-50">
                      {ticket.subject}
                    </h4>
                    <p className="text-xs text-gray-600 dark:text-tech-400 mt-1">
                      #{ticket.ticketNumber} • {ticket.status} • {ticket.priority}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-tech-500 mt-1">
                      Press to view details and respond
                    </p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${ticket.status === 'OPEN' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' :
                      ticket.status === 'IN_PROGRESS' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400' :
                        ticket.status === 'RESOLVED' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400' :
                          'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400'
                    }`}>
                    {ticket.status}
                  </span>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8">
              <LifebuoyIcon className="w-12 h-12 text-gray-400 dark:text-tech-500 mx-auto mb-4" />
              <p className="text-body text-gray-600 dark:text-tech-400">No support tickets yet</p>
              <p className="text-sm text-gray-500 dark:text-tech-500 mt-1">
                Create a ticket to get help from your organization's administrators
              </p>
            </div>
          )}
        </div>

        {supportTickets.length > 3 && (
          <div className="mt-4 text-center">
            <button
              onClick={() => toast('Full ticket history coming soon!')}
              className="text-blue-500 hover:text-blue-600 text-sm font-medium"
            >
              View all {supportTickets.length} tickets
            </button>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="premium-card p-6">
        <h3 className="text-heading-lg text-gray-900 dark:text-tech-50 font-display mb-6">Quick Actions</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <button
            onClick={() => navigate('/status')}
            className="flex items-center space-x-3 p-4 bg-gradient-to-r from-success to-accent-emerald text-white rounded-lg hover:shadow-glow-lg transition-all duration-300 hover:-translate-y-0.5"
          >
            <ShieldCheckIcon className="w-5 h-5" />
            <span className="text-body-sm font-medium">Check Status</span>
          </button>
          <button
            onClick={() => navigate('/reports')}
            className="flex items-center space-x-3 p-4 bg-gradient-to-r from-secondary-500 to-secondary-600 text-white rounded-lg hover:shadow-glow-blue transition-all duration-300 hover:-translate-y-0.5"
          >
            <DocumentChartBarIcon className="w-5 h-5" />
            <span className="text-body-sm font-medium">View Reports</span>
          </button>
          <button
            onClick={() => navigate('/locations')}
            className="flex items-center space-x-3 p-4 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-lg hover:shadow-glow transition-all duration-300 hover:-translate-y-0.5"
          >
            <MapPinIcon className="w-5 h-5" />
            <span className="text-body-sm font-medium">View Locations</span>
          </button>
        </div>
      </div>

      {/* Logout Section */}
      <div className="premium-card p-6">
        <h3 className="text-heading-lg text-gray-900 dark:text-tech-50 font-display mb-6">Account</h3>
        <div className="space-y-4">
          {/* Dev Mode Toggle */}
          <div className="p-4 border-2 border-orange-500/30 bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 rounded-lg bg-orange-500/20">
                  <BeakerIcon className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                </div>
                <div>
                  <h4 className="text-lg font-bold text-orange-800 dark:text-orange-200">Enable Dev Mode</h4>
                  <p className="text-sm text-orange-600 dark:text-orange-400">Show developer controls in Status page</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={isDevModeEnabled}
                  onChange={(e) => setDevModeEnabled(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 dark:peer-focus:ring-orange-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-orange-600"></div>
              </label>
            </div>
          </div>

          <button
            onClick={handleLogout}
            disabled={logoutLoading}
            className="w-full flex items-center justify-center space-x-3 p-4 bg-gradient-to-r from-error to-red-600 text-white rounded-lg hover:shadow-glow-lg transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0"
          >
            {logoutLoading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
            ) : (
              <ArrowRightOnRectangleIcon className="w-5 h-5" />
            )}
            <span className="text-body-sm font-medium">
              {logoutLoading ? 'Signing out...' : 'Sign Out'}
            </span>
          </button>
        </div>
      </div>

      {/* Support Ticket Modal */}
      {showSupportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white dark:bg-tech-900 rounded-lg shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-tech-50">Create Support Ticket</h3>
                <button
                  onClick={() => setShowSupportModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-tech-300"
                >
                  <XCircleIcon className="w-6 h-6" />
                </button>
              </div>

              {userLocations.length === 0 && (
                <div className="mb-4 p-4 border border-orange-200 bg-orange-50 dark:bg-orange-900/20 dark:border-orange-800 rounded-lg">
                  <div className="flex items-start space-x-3">
                    <ExclamationTriangleIcon className="w-5 h-5 text-orange-600 dark:text-orange-400 mt-0.5" />
                    <div>
                      <h4 className="text-sm font-bold text-orange-800 dark:text-orange-200">Quick Request</h4>
                      <p className="text-sm text-orange-600 dark:text-orange-400 mt-1">
                        Need location access? Use "Request Location Assignment" as your subject to fast-track your request.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmitSupport(handleCreateSupportTicket)} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-tech-300 mb-2">
                    Subject
                  </label>
                  <input
                    type="text"
                    {...registerSupport('subject', { required: 'Subject is required' })}
                    placeholder={userLocations.length === 0 ? "Request Location Assignment" : "Brief description of your issue"}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-tech-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-tech-800 text-gray-900 dark:text-tech-50"
                  />
                  {supportErrors.subject && (
                    <p className="text-red-500 text-sm mt-1">{supportErrors.subject.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-tech-300 mb-2">
                    Priority
                  </label>
                  <select
                    {...registerSupport('priority', { required: 'Priority is required' })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-tech-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-tech-800 text-gray-900 dark:text-tech-50"
                  >
                    <option value="LOW">Low</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="HIGH">High</option>
                  </select>
                  {supportErrors.priority && (
                    <p className="text-red-500 text-sm mt-1">{supportErrors.priority.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-tech-300 mb-2">
                    Description
                  </label>
                  <textarea
                    {...registerSupport('description', { required: 'Description is required' })}
                    rows={4}
                    placeholder={userLocations.length === 0 ?
                      "Please assign me to locations so I can start working. I need access to [specify which locations if you know them]." :
                      "Detailed description of your issue or request"
                    }
                    className="w-full px-3 py-2 border border-gray-300 dark:border-tech-700 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-tech-800 text-gray-900 dark:text-tech-50 resize-none"
                  />
                  {supportErrors.description && (
                    <p className="text-red-500 text-sm mt-1">{supportErrors.description.message}</p>
                  )}
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowSupportModal(false)}
                    className="flex-1 px-4 py-2 text-gray-700 dark:text-tech-300 border border-gray-300 dark:border-tech-700 rounded-lg hover:bg-gray-50 dark:hover:bg-tech-800 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSupportSubmitting}
                    className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 transition-colors"
                  >
                    {isSupportSubmitting ? 'Creating...' : 'Create Ticket'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Ticket Detail Modal */}
      {showTicketDetail && selectedTicketId && (
        <TicketDetailModal
          ticketId={selectedTicketId}
          isOpen={showTicketDetail}
          onClose={handleCloseTicketDetail}
          onUpdate={handleTicketUpdate}
        />
      )}

      {/* Bottom spacing to prevent footer coverage */}
      <div className="h-24"></div>
    </div>
  )
}